import { useCallback, useEffect, useRef } from 'react';
import { alpha } from '@mui/material/styles';

const DATA_ATTR = 'data-auto-highlight-active';
const DEFAULT_DURATION = 1000;

export function useAutoHighlight<T, E extends HTMLElement = HTMLDivElement>(observed: T | undefined) {
    const elementRef = useRef<E | null>(null);
    const timerRef = useRef<number | null>(null);
    const first = useRef(true);
    const manualRef = useRef(false);
    const manualSessionActiveRef = useRef(false);
    const prev = useRef<T | undefined>(observed);

    const cleanupTimer = useCallback(() => {
        if (!timerRef.current) return;
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    const setActiveAttr = useCallback((value: boolean) => {
        const node = elementRef.current;
        if (!node) return;
        node.setAttribute(DATA_ATTR, value ? 'true' : 'false');
    }, []);

    const deactivate = useCallback(() => {
        cleanupTimer();
        setActiveAttr(false);
    }, [cleanupTimer, setActiveAttr]);

    const activate = useCallback(() => {
        const node = elementRef.current;
        if (!node) return;

        setActiveAttr(true);
        cleanupTimer();

        timerRef.current = window.setTimeout(() => {
            setActiveAttr(false);
            timerRef.current = null;
        }, DEFAULT_DURATION);
    }, [cleanupTimer, setActiveAttr]);

    const endAfterRaf = useCallback(() => {
        let left = Math.max(0, 2 | 0);
        const tick = () => {
            if (left <= 0) {
                manualRef.current = false;
                manualSessionActiveRef.current = false;
                return;
            }
            left -= 1;
            requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, []);

    const markManual = useCallback(() => {
        manualRef.current = true;
        deactivate();

        if (manualSessionActiveRef.current) return;
        manualSessionActiveRef.current = true;

        const end = () => endAfterRaf();

        window.addEventListener('pointerup', end, { once: true });
        window.addEventListener('mouseup', end, { once: true });
        window.addEventListener('touchend', end, { once: true });
        window.addEventListener('keyup', end, { once: true });
    }, [deactivate, endAfterRaf]);

    const setRef = useCallback(
        (node: E | null) => {
            elementRef.current = node;
            if (node) {
                node.setAttribute(DATA_ATTR, node.getAttribute(DATA_ATTR) ?? 'false');
            } else {
                cleanupTimer();
            }
        },
        [cleanupTimer]
    );

    useEffect(() => {
        if (observed === undefined) return;

        if (first.current) {
            first.current = false;
            prev.current = observed;
            return;
        }

        if (Object.is(observed, prev.current)) return;

        if (manualRef.current) {
            deactivate();
            prev.current = observed;
            return;
        }

        activate();

        prev.current = observed;
    }, [activate, deactivate, observed]);

    useEffect(
        () => () => {
            cleanupTimer();
        },
        [cleanupTimer]
    );

    return { ref: setRef, markManual };
}

type HighlightOptions = {
    transitionMs?: number;
    alphaValue?: number;
    highlightSelf?: boolean;
    highlightSelectors?: string[];
};

export function highlightSx(opts?: HighlightOptions) {
    const transitionMs = opts?.transitionMs ?? 700;
    const a = opts?.alphaValue ?? 0.85;
    const highlightSelf = opts?.highlightSelf ?? false;
    const selectors = opts?.highlightSelectors ?? [];

    return (theme: any) => {
        const tint = alpha(theme.palette.secondary.main, a);

        const baseTransitions: Record<string, any> = {
            transition: `color ${transitionMs}ms ease`,
        };

        const normalizedSelectors = selectors
            .map((selector) => selector.trim())
            .filter((selector) => selector.length > 0)
            .map((selector) =>
                selector.startsWith('&')
                    ? { base: selector, active: selector.replace(/^&/, `&[${DATA_ATTR}="true"]`) }
                    : { base: `& ${selector}`, active: `&[${DATA_ATTR}="true"] ${selector}` }
            );

        normalizedSelectors.forEach(({ base }) => {
            baseTransitions[base] = {
                ...(baseTransitions[base] ?? {}),
                transition: `color ${transitionMs}ms ease`,
            };
        });

        baseTransitions['& .MuiRadio-root, & .MuiCheckbox-root, & .MuiFormControlLabel-root .MuiFormControlLabel-label'] = {
            transition: `color ${transitionMs}ms ease`,
        };
        baseTransitions['& .MuiSlider-thumb, & .MuiSlider-track'] = {
            transition: `color ${transitionMs}ms ease, background-color ${transitionMs}ms ease, border-color ${transitionMs}ms ease`,
        };
        baseTransitions['&.auto-highlight-text'] = {
            ...(baseTransitions['&.auto-highlight-text'] ?? {}),
            transition: `color ${transitionMs}ms ease`,
        };
        baseTransitions['&.auto-highlight-text strong'] = {
            ...(baseTransitions['&.auto-highlight-text strong'] ?? {}),
            transition: `color ${transitionMs}ms ease`,
        };

        const activeContainer = `&[${DATA_ATTR}="true"]`;

        const activeStyles: Record<string, any> = {};

        if (highlightSelf) {
            activeStyles[activeContainer] = {
                ...(activeStyles[activeContainer] ?? {}),
                color: tint,
            };
        }

        normalizedSelectors.forEach(({ active }) => {
            activeStyles[active] = {
                ...(activeStyles[active] ?? {}),
                color: tint,
            };
        });

        activeStyles[`${activeContainer}.auto-highlight-text`] = {
            ...(activeStyles[`${activeContainer}.auto-highlight-text`] ?? {}),
            color: tint,
        };
        activeStyles[`${activeContainer}.auto-highlight-text strong`] = {
            ...(activeStyles[`${activeContainer}.auto-highlight-text strong`] ?? {}),
            color: tint,
        };

        // RADIO & CHECKBOX
        activeStyles[`${activeContainer} .MuiRadio-root.Mui-checked, ${activeContainer} .MuiCheckbox-root.Mui-checked`] = {
            color: tint,
        };
        activeStyles[`${activeContainer} .MuiCheckbox-root`] = {
            color: tint,
        };
        activeStyles[`${activeContainer} .MuiFormControlLabel-root .Mui-checked + .MuiFormControlLabel-label`] = {
            color: tint,
        };
        activeStyles[`${activeContainer} .MuiFormControlLabel-root .MuiCheckbox-root + .MuiFormControlLabel-label`] = {
            color: tint,
        };

        // SLIDER
        activeStyles[`${activeContainer} .MuiSlider-thumb, ${activeContainer} .MuiSlider-track`] = {
            color: tint,
        };

        return { ...baseTransitions, ...activeStyles };
    };
}
